import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { ImageDataDb } from '../_shared/data/image-data-db.data';
import { LinkDirective } from '../_shared/directives/link.directive';
import type { BannerVariant, IBanner } from '../_shared/models/banner.model';
import type { IButton } from '../_shared/models/button/button.model';
import type { IDynamicElement } from '../_shared/models/dynamic/dynamic-element.model';
import type { MediaImage } from '../_shared/models/media/media-image.model';
import type { BannerLayout } from '../_shared/types/banner-layout.type';
import type { ButtonType } from '../_shared/types/button/button.type';
import type { SectionSpacing } from '../_shared/types/section-spacing.type';
import type { SurfaceMode } from '../_shared/types/surface-mode.type';
import { ButtonComponent } from '../button/button.component';
import { ContainerComponent } from '../container/container.component';
import { HeadingComponent } from '../heading/heading.component';
import { ImageItemComponent } from '../media/media-item/image-item/image-item.component';
import { TextHtmlComponent } from '../text-html/text-html.component';

const BANNER_VARIANT_MODE: Record<BannerVariant, SurfaceMode> = { sand: 'light', silver: 'light', blue: 'dark' };

@Component({
    selector: 'sirius-banner',
    templateUrl: './banner.component.html',
    styleUrls: ['./banner.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        ContainerComponent,
        HeadingComponent,
        ImageItemComponent,
        LinkDirective,
        TextHtmlComponent,
    ],
    host: {
        '[class.sir-banner-variant-sand]': 'variant === "sand"',
        '[class.sir-banner-variant-silver]': 'variant === "silver"',
        '[class.sir-banner-variant-blue]': 'variant === "blue"',
        '[class.is-light]': 'surfaceMode === "light"',
        '[class.is-dark]': 'surfaceMode === "dark"',
        '[class.is-full-width]': 'layout === "full-width"',
        '[class.has-background-image]': '!!backgroundImage',
    },
})
export class BannerComponent implements OnInit, IDynamicElement {
    @Input() title = '';
    @Input() textHtml = '';
    @Input() button?: IButton;
    @Input() variant: BannerVariant = 'sand';
    @Input() layout: BannerLayout = 'contained';
    @Input() backgroundImage?: MediaImage;

    config?: IBanner;

    protected readonly imageData = ImageDataDb.hero.image;

    static sectionSpacing(config: IBanner): SectionSpacing {
        return config.layout === 'full-width' ? 'flush' : 'default';
    }

    get surfaceMode(): SurfaceMode {
        return this.backgroundImage ? 'dark' : BANNER_VARIANT_MODE[this.variant];
    }

    get buttonType(): ButtonType {
        return this.surfaceMode === 'dark' ? 'secondary' : 'primary';
    }

    ngOnInit(): void {
        if (this.config) {
            this.title = this.config.title;
            this.textHtml = this.config.textHtml;
            this.button = this.config.button;
            this.variant = this.config.variant ?? 'sand';
            this.layout = this.config.layout ?? 'contained';
            this.backgroundImage = this.config.backgroundImage;
        }
    }
}
